import React, { createContext, useContext, useReducer, useCallback } from 'react'
import type { AITaskRequest, AITaskResponse, AITaskType } from '@/types'

interface AITask {
  id: string
  type: AITaskType
  request: AITaskRequest
  response?: AITaskResponse
  status: 'pending' | 'completed' | 'failed'
  timestamp: Date
}

interface AIContextType {
  // State
  isProcessing: boolean
  tasks: AITask[]
  currentTask: AITask | null
  
  // Actions
  processAITask: (request: AITaskRequest, type: AITaskType) => Promise<AITaskResponse>
  clearTasks: () => void
  getTaskHistory: () => AITask[]
}

const AIContext = createContext<AIContextType | undefined>(undefined)

type AIAction =
  | { type: 'START_PROCESSING'; payload: AITask }
  | { type: 'COMPLETE_TASK'; payload: { id: string; response: AITaskResponse } }
  | { type: 'FAIL_TASK'; payload: { id: string; error: string } }
  | { type: 'CLEAR_TASKS' }

interface AIStateType {
  isProcessing: boolean
  tasks: AITask[]
  currentTask: AITask | null
}

const initialState: AIStateType = {
  isProcessing: false,
  tasks: [],
  currentTask: null,
}

function aiReducer(state: AIStateType, action: AIAction): AIStateType {
  switch (action.type) {
    case 'START_PROCESSING':
      return {
        ...state,
        isProcessing: true,
        currentTask: action.payload,
        tasks: [action.payload, ...state.tasks],
      }
    
    case 'COMPLETE_TASK':
      return {
        ...state,
        isProcessing: false,
        currentTask: null,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id
            ? { ...task, response: action.payload.response, status: 'completed' as const }
            : task
        ),
      }
    
    case 'FAIL_TASK':
      return {
        ...state,
        isProcessing: false,
        currentTask: null,
        tasks: state.tasks.map(task =>
          task.id === action.payload.id
            ? { ...task, status: 'failed' as const }
            : task
        ),
      }
    
    case 'CLEAR_TASKS':
      return {
        ...state,
        tasks: [],
        currentTask: null,
      }
    
    default:
      return state
  }
}

// Mock AI service for development
class MockAIService {
  async processTask(request: AITaskRequest, type: AITaskType): Promise<AITaskResponse> {
    // Simulate processing delay
    await new Promise(resolve => setTimeout(resolve, 1500 + Math.random() * 1000))
    
    // Mock responses based on task type
    let result = ''
    switch (type) {
      case 'proofread':
        result = this.mockProofread(request.text)
        break
      case 'rewrite':
        result = this.mockRewrite(request.text, request.instructions)
        break
      case 'expand':
        result = this.mockExpand(request.text)
        break
      case 'generate':
        result = this.mockGenerate(request.text, request.instructions)
        break
      default:
        result = request.text
    }
    
    return {
      task_type: type,
      original_text: request.text,
      result,
      success: true,
    }
  }
  
  private mockProofread(text: string): string {
    // Simple mock proofreading
    return text
      .replace(/\bteh\b/g, 'the')
      .replace(/\brecieve\b/g, 'receive')
      .replace(/\boccured\b/g, 'occurred')
      .replace(/\baccomodate\b/g, 'accommodate')
      .replace(/\bsepererate\b/g, 'separate')
      .replace(/\s+/g, ' ')
      .trim()
  }
  
  private mockRewrite(text: string, instructions?: string): string {
    if (instructions?.toLowerCase().includes('formal')) {
      return text
        .replace(/\bcan't\b/g, 'cannot')
        .replace(/\bwon't\b/g, 'will not')
        .replace(/\bdon't\b/g, 'do not')
        .replace(/\bisn't\b/g, 'is not')
        .replace(/\bI think\b/g, 'It is believed')
        .replace(/\bmaybe\b/g, 'perhaps')
    }
    
    if (instructions?.toLowerCase().includes('casual')) {
      return text
        .replace(/\bcannot\b/g, "can't")
        .replace(/\bwill not\b/g, "won't")
        .replace(/\bdo not\b/g, "don't")
        .replace(/\bis not\b/g, "isn't")
    }
    
    // Default rewrite - improve clarity
    return text
      .replace(/\bvery\s+(\w+)/g, 'extremely $1')
      .replace(/\ba lot of\b/g, 'many')
      .replace(/\bin order to\b/g, 'to')
  }
  
  private mockExpand(text: string): string {
    // Add explanatory phrases and examples
    const sentences = text.split('. ').filter(s => s.trim())
    
    return sentences.map(sentence => {
      if (sentence.length < 50) {
        return sentence + ', which demonstrates the importance of this concept in scientific research'
      }
      return sentence
    }).join('. ') + '. Furthermore, this analysis provides valuable insights into the underlying mechanisms and potential applications in various fields.'
  }
  
  private mockGenerate(prompt: string, instructions?: string): string {
    // Generate content based on prompt
    const templates = [
      `Based on ${prompt}, it is important to consider the following aspects: methodology, data analysis, and interpretation of results.`,
      `The concept of ${prompt} has significant implications for our understanding of scientific principles and their practical applications.`,
      `Recent research on ${prompt} has revealed new insights that challenge traditional assumptions and open new avenues for investigation.`,
    ]
    
    return templates[Math.floor(Math.random() * templates.length)]
  }
}

const mockAIService = new MockAIService()

export const AIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(aiReducer, initialState)

  const processAITask = useCallback(async (request: AITaskRequest, type: AITaskType): Promise<AITaskResponse> => {
    const task: AITask = {
      id: `task-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type,
      request,
      status: 'pending',
      timestamp: new Date(),
    }

    dispatch({ type: 'START_PROCESSING', payload: task })

    try {
      // Use real API in production, mock in development
      const { aiAPI } = await import('@/api/ai')
      const response = await aiAPI.processTask(request, type)
      
      dispatch({ 
        type: 'COMPLETE_TASK', 
        payload: { id: task.id, response } 
      })
      
      return response
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      
      dispatch({ 
        type: 'FAIL_TASK', 
        payload: { id: task.id, error: errorMessage } 
      })
      
      throw error
    }
  }, [])

  const clearTasks = useCallback(() => {
    dispatch({ type: 'CLEAR_TASKS' })
  }, [])

  const getTaskHistory = useCallback(() => {
    return state.tasks.filter(task => task.status === 'completed')
  }, [state.tasks])

  const value: AIContextType = {
    // State
    isProcessing: state.isProcessing,
    tasks: state.tasks,
    currentTask: state.currentTask,
    
    // Actions
    processAITask,
    clearTasks,
    getTaskHistory,
  }

  return (
    <AIContext.Provider value={value}>
      {children}
    </AIContext.Provider>
  )
}

export const useAIStore = () => {
  const context = useContext(AIContext)
  if (context === undefined) {
    throw new Error('useAIStore must be used within an AIProvider')
  }
  return context
}